import { useState } from 'react';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { useFormLoadTime } from '../hooks/useFormLoadTime';
import { getApiBaseUrl } from '../utils/api';
import { getTrackingDataForSubmission } from '../utils/trackingParams';

export default function CallbackForm({ compact = false }) {
  const executeRecaptcha = useRecaptcha('callback_form');
  const formLoadTime = useFormLoadTime();
  const API_URL = getApiBaseUrl();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const recaptchaToken = await executeRecaptcha();
      const form = e.target;
      const tracking = getTrackingDataForSubmission();

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.elements.name?.value || '',
          phone: form.elements.phone.value,
          email: '', // Not collected for callback
          project_type: 'Call-back Request',
          message: 'Requested a call back from the website',
          form_load_time: formLoadTime,
          recaptcha_token: recaptchaToken,
          honeypot: '',
          ...tracking
        }),
      });

      if (response.ok) {
        // Fire conversion event
        try {
          if (window.gtag) {
            const phone = form.elements.phone.value;
            const normalizedPhone = phone.startsWith('+') ? phone : `+91${phone.replace(/\D/g, '')}`;
            
            window.gtag('set', 'user_data', { phone_number: normalizedPhone });
            window.gtag('event', 'conversion', {
              'send_to': 'AW-16469356373/3oU5CObTnKIdENXema09'
            });
          }
        } catch (conversionError) {
          console.error('Conversion tracking error:', conversionError);
        }

        setSuccess(true);
        form.reset();
        
        setTimeout(() => setSuccess(false), 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to submit. Please try calling us directly.');
      }
    } catch (err) {
      console.error('Callback form error:', err);
      setError('Network error. Please call us at +91 92058 91804');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`${compact ? 'p-3' : 'p-6'} bg-green-50 border border-green-200 rounded-lg`}>
        <p className="text-green-800 font-medium">✓ Thank you! We'll call you back soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={compact ? 'space-y-2' : 'space-y-4'}>
      {!compact && (
        <h3 className="text-lg font-semibold">Request a Call Back</h3>
      )}
      
      <input
        type="text"
        name="name"
        placeholder="Your name (optional)"
        className={`w-full px-3 ${compact ? 'py-2 text-sm' : 'py-2.5'} border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent`}
      />
      
      <input
        type="tel"
        name="phone"
        placeholder="Phone number *"
        required
        className={`w-full px-3 ${compact ? 'py-2 text-sm' : 'py-2.5'} border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent`}
      />
      
      {error && (
        <p className="text-red-600 text-sm">{error}</p>
      )}
      
      <button
        type="submit"
        disabled={loading}
        className={`w-full bg-black text-white ${compact ? 'py-2 text-sm' : 'py-2.5'} rounded font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
      >
        {loading ? 'Sending...' : 'Request Call Back'}
      </button>
      
      <p className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}>
        We'll call you within 24 hours
      </p>
    </form>
  );
}
